import { InboxItem } from "@cohesive/api-client";
import { defaultChatIcon, IconWithBackground } from "@cohesive/ui-components";
import { format, isToday, isYesterday } from "date-fns";
import React, { useMemo, useState } from "react";

import { useUpdateInboxItem } from "@/api/inbox";
import { RenameDialog } from "@/components/Dialogs/RenameDialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { flowSessionModeIcon } from "@/constants/flow-session-modes";
import { useFlowSessionActions } from "@/hooks/useFlowSessionActions";
import { cn } from "@/utils";
import {
  groupInboxItemsByDate,
  sortGroupedSessions,
} from "@/utils/sessionGrouping";
import { cx } from "class-variance-authority";
import { Archive, MoreHorizontal, Pencil } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import Typewriter from "../Typewriter/Typewriter";

interface RecentChatsProps {
  inboxItems: InboxItem[];
  maxItems?: number;
  onFlowSessionClick: (session: InboxItem) => void;
  groupByDate?: boolean;
}

export function getIconForFlowSession(inboxItem: InboxItem) {
  return flowSessionModeIcon(inboxItem?.flow_session?.mode) ?? defaultChatIcon;
}

export const RecentChats: React.FC<RecentChatsProps> = ({
  inboxItems,
  maxItems,
  onFlowSessionClick,
  groupByDate = false,
}) => {
  const formatSessionDate = (dateString: string) => {
    const date = new Date(dateString);
    const timeFormat = "h:mm a";

    if (isToday(date)) {
      return `Today ${format(date, timeFormat)}`;
    } else if (isYesterday(date)) {
      return `Yesterday ${format(date, timeFormat)}`;
    }
    return format(date, "MMM d, yyyy h:mm a");
  };

  const groupedSessions = useMemo(() => {
    if (!groupByDate) return null;
    return groupInboxItemsByDate(inboxItems);
  }, [inboxItems, groupByDate]);

  if (!groupByDate) {
    const recentSessions = inboxItems
      .filter((item) => item.flow_session?.status !== "initialized")
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      )
      .slice(0, typeof maxItems === "number" ? maxItems : undefined);

    return (
      <div className="flex flex-col space-y-1">
        <AnimatePresence>
          {recentSessions.map((session, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{
                opacity: 0,
                y: 0,
                height: 0,
                marginTop: 0,
                marginBottom: 0,
              }}
              transition={{ delay: index * 0.025 }}
            >
              <ChatItem
                inboxItem={session}
                onFlowSessionClick={onFlowSessionClick}
                formatSessionDate={formatSessionDate}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-4">
      <AnimatePresence>
        {sortGroupedSessions(Object.entries(groupedSessions || {})).map(
          ([groupName, sessions]) => {
            return (
              <div key={groupName}>
                <div className="space-y-1">
                  <h3 className="text-sm font-medium mb-2">{groupName}</h3>
                  {sessions.map((session) => (
                    <div id={session.id} key={session.id}>
                      <ChatItem
                        inboxItem={session}
                        onFlowSessionClick={onFlowSessionClick}
                        formatSessionDate={formatSessionDate}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          },
        )}
      </AnimatePresence>
    </div>
  );
};

const ChatItem: React.FC<{
  inboxItem: InboxItem;
  onFlowSessionClick: (inboxItem: InboxItem) => void;
  formatSessionDate: (dateString: string) => string;
}> = ({ inboxItem, onFlowSessionClick, formatSessionDate }) => {
  const [open, setOpen] = useState(false);
  const icon = getIconForFlowSession(inboxItem);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const { handleRename } = useFlowSessionActions();

  const { mutateAsync: updateInboxItem } = useUpdateInboxItem();

  return (
    <div
      className={cx(
        "flex items-center justify-between px-1 py-1 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 rounded-full group",
        {
          "bg-neutral-50 dark:bg-neutral-800/50": open,
        },
      )}
    >
      <button
        className="flex items-center flex-1 cursor-pointer text-left @container"
        onClick={() => onFlowSessionClick(inboxItem)}
      >
        <div className="shrink-0 relative">
          {icon && <IconWithBackground icon={icon} size={30} />}{" "}
        </div>
        <div className="flex flex-col @md:flex-row @md:items-center @md:justify-between ml-3 min-w-0 flex-1">
          <span
            className={cn("text-sm truncate", {
              shimmer:
                inboxItem.flow_session?.status === "processing" ||
                inboxItem.flow_session?.name === "",
              "font-semibold": inboxItem.is_read === false,
            })}
          >
            <Typewriter
              text={inboxItem.flow_session?.name || "New Chat"}
              enabled={!!inboxItem.flow_session?.typewrite}
              speed={25}
            />
          </span>
          <span className="text-xs text-neutral-500 @md:ml-3 whitespace-nowrap">
            {formatSessionDate(inboxItem.created_at)}
          </span>
        </div>
      </button>

      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cx(
              "rounded-full h-8 w-8 p-0 opacity-0 group-hover:opacity-100",
              {
                "opacity-100": open,
              },
            )}
          >
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setIsRenameModalOpen(true)}>
            <Pencil />
            <span>Rename</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              updateInboxItem({
                id: inboxItem.id,
                data: {
                  is_archived: true,
                },
              })
            }
            className="text-lc-blocked focus:text-lc-blocked dark:text-lc-blocked dark:focus:text-lc-blocked"
          >
            <Archive />
            <span>Archive</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <RenameDialog
        open={isRenameModalOpen}
        onOpenChange={setIsRenameModalOpen}
        initialValues={{
          name: inboxItem.flow_session?.name || "New Chat",
        }}
        onSubmit={(values) => {
          handleRename(inboxItem.flow_session, values.name);
          setIsRenameModalOpen(false);
        }}
      />
    </div>
  );
};
